import {
  getAccessTokenFromResponse,
  getTokenPayload,
  shouldStoreRefreshToken,
} from "@/utils/auth-security-policy.mjs";

const PERSISTED_AUTH_KEY = "auth";
const TOKEN_KEYS = ["refresh_token", "access_token", "id_token"];
const DEFAULT_AUTH_REFRESH_MODE = "legacy";

export function isCookieRefreshEnabled(authRefreshMode = DEFAULT_AUTH_REFRESH_MODE) {
  return authRefreshMode === "cookie";
}

export function isGoogleUser(userInfo = {}) {
  return userInfo && userInfo.user_type === "google";
}

export function shouldPersistAuthTokens(state = {}) {
  return !isCookieRefreshEnabled(state.auth_refresh_mode) || isGoogleUser(state.user_info);
}

export function getAuthPersistedState(state) {
  const persistedState = {
    auth_refresh_mode: state.auth_refresh_mode || DEFAULT_AUTH_REFRESH_MODE,
    user_info: state.user_info,
    userEmailInput_remembered: state.userEmailInput_remembered,
  };

  if (shouldPersistAuthTokens(state)) {
    persistedState.refresh_token = state.refresh_token;
    persistedState.access_token = state.access_token;
    persistedState.id_token = state.id_token;
  }

  return persistedState;
}

export function purgePersistedAuthTokens(storage = window.localStorage, authRefreshMode = null) {
  if (!storage) return;

  const rawAuth = storage.getItem(PERSISTED_AUTH_KEY);
  if (!rawAuth) return;

  try {
    const auth = JSON.parse(rawAuth);
    const nextAuthRefreshMode = authRefreshMode || auth.auth_refresh_mode || DEFAULT_AUTH_REFRESH_MODE;
    auth.auth_refresh_mode = nextAuthRefreshMode;

    if (shouldPersistAuthTokens({ ...auth, auth_refresh_mode: nextAuthRefreshMode })) {
      storage.setItem(PERSISTED_AUTH_KEY, JSON.stringify(auth));
      return;
    }

    let hasToken = false;

    TOKEN_KEYS.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(auth, key)) {
        delete auth[key];
        hasToken = true;
      }
    });

    if (hasToken || authRefreshMode) {
      storage.setItem(PERSISTED_AUTH_KEY, JSON.stringify(auth));
    }
  } catch {
    storage.removeItem(PERSISTED_AUTH_KEY);
  }
}

export function applyAuthTokensFromResponse(store, data = {}) {
  const token = getTokenPayload(data);
  const accessToken = getAccessTokenFromResponse(data);
  const idToken = token.id_token || token.idToken;

  if (accessToken) {
    store.commit("setAccessToken", accessToken);
  }
  if (idToken) {
    store.commit("setIdToken", idToken);
  }
}

export { getAccessTokenFromResponse, shouldStoreRefreshToken };

export function getCookieRefreshConfig() {
  return { skipAuth: true, withCredentials: true };
}

export function getRefreshTokenConfig(refreshToken, authRefreshMode = DEFAULT_AUTH_REFRESH_MODE) {
  if (isCookieRefreshEnabled(authRefreshMode)) {
    return getCookieRefreshConfig();
  }

  return {
    skipAuth: true,
    headers: {
      Authorization: `Bearer ${refreshToken}`,
    },
  };
}
